/**
 * Billiards (8-Ball) Game Engine
 *
 * Game rules and state management for 8-Ball Pool.
 * Handles: initialization, ball rack, shot processing, foul detection,
 * assignment logic, turn management, and win/loss detection.
 */

import {
  BilliardsGameState,
  BilliardsPlayer,
  BilliardsBall,
  BallType,
  PlayerAssignment,
  Shot,
  ShotResult,
  FoulType,
  BilliardsPlayerType,
  TABLE_WIDTH,
  TABLE_HEIGHT,
  CUSHION_WIDTH,
  BALL_RADIUS,
  CUE_BALL_RADIUS,
  getBallType,
} from './types';
import { BilliardsPhysics } from './BilliardsPhysics';

export class BilliardsEngine {
  private state: BilliardsGameState;
  private physics: BilliardsPhysics;

  constructor() {
    this.physics = new BilliardsPhysics();
    this.state = this.createInitialState();
  }

  // =========================================================================
  // Initialization
  // =========================================================================

  private createInitialState(): BilliardsGameState {
    return {
      gameId: `billiards_${Date.now()}_${Math.random().toString(36).substring(7)}`,
      balls: [],
      currentTurn: 'player1',
      player1Assignment: 'unassigned',
      player2Assignment: 'unassigned',
      player1PocketedBalls: [],
      player2PocketedBalls: [],
      isBreakShot: true,
      tableOpen: true,
      gameStatus: { type: 'active' },
      playerTypes: { player1: 'human', player2: 'human' },
      moveHistory: [],
      startedAt: Date.now(),
      lastMoveAt: Date.now(),
      ballInHand: false,
      railContactAfterHit: false,
    };
  }

  initializeGame(playerTypes: Record<BilliardsPlayer, BilliardsPlayerType>): void {
    this.state = this.createInitialState();
    this.state.playerTypes = playerTypes;
    this.state.balls = this.createRack();
    this.setupPhysicsBodies();
  }

  /**
   * Create the standard 8-ball rack plus the cue ball.
   * Triangle rack at the foot spot with specific ball placement.
   */
  private createRack(): BilliardsBall[] {
    const balls: BilliardsBall[] = [];

    // Cue ball position (head spot area)
    const cueX = TABLE_WIDTH * 0.25;
    const cueY = TABLE_HEIGHT / 2;

    balls.push({
      id: 'cue',
      number: 0,
      type: 'cue',
      x: cueX,
      y: cueY,
      vx: 0,
      vy: 0,
      radius: CUE_BALL_RADIUS,
      mass: 1,
      pocketed: false,
    });

    // Rack position (foot spot)
    const rackX = TABLE_WIDTH * 0.73;
    const rackY = TABLE_HEIGHT / 2;
    const spacing = BALL_RADIUS * 2.05; // Slight gap between balls

    // Standard 8-ball rack arrangement (5 rows)
    // 8-ball must be in center, one solid and one stripe in corners of last row
    const rackOrder = [
      1,           // Row 1: 1 ball
      9, 2,        // Row 2: stripe, solid
      3, 8, 10,    // Row 3: solid, 8-ball, stripe
      11, 4, 12, 5,   // Row 4: mix
      6, 13, 14, 7, 15 // Row 5: mix
    ];

    let ballIndex = 0;
    for (let row = 0; row < 5; row++) {
      const ballsInRow = row + 1;
      const rowX = rackX + row * spacing * Math.cos(Math.PI / 6);
      const startY = rackY - (ballsInRow - 1) * BALL_RADIUS;

      for (let col = 0; col < ballsInRow; col++) {
        const ballNumber = rackOrder[ballIndex];
        const x = rowX;
        const y = startY + col * BALL_RADIUS * 2;

        balls.push({
          id: `ball_${ballNumber}`,
          number: ballNumber,
          type: getBallType(ballNumber),
          x,
          y,
          vx: 0,
          vy: 0,
          radius: BALL_RADIUS,
          mass: 1,
          pocketed: false,
        });

        ballIndex++;
      }
    }

    return balls;
  }

  private setupPhysicsBodies(): void {
    this.physics.clearBalls();

    for (const ball of this.state.balls) {
      this.physics.addBall({ ...ball });
    }
  }

  // =========================================================================
  // Shot Processing
  // =========================================================================

  /**
   * Execute a shot on the cue ball.
   */
  executeShot(shot: Shot): void {
    this.physics.applyShot(shot.angle, shot.power, shot.spin);
  }

  /**
   * Get the physics engine for running the simulation loop.
   */
  getPhysics(): BilliardsPhysics {
    return this.physics;
  }

  /**
   * Process the results after all balls have stopped.
   * Returns the shot result and advances the turn.
   */
  processResults(shot: Shot): ShotResult {
    const player = this.state.currentTurn;
    const physicsBalls = this.physics.getBalls();

    // Gather pocketed balls this shot
    const pocketedBalls: BilliardsBall[] = [];
    let cueBallPocketed = false;
    let eightBallPocketed = false;
    let ownBallPocketed = false;

    const playerAssignment = this.getPlayerAssignment(player);

    for (const pBall of physicsBalls) {
      if (pBall.pocketed) {
        const existingBall = this.state.balls.find((b) => b.id === pBall.id);
        if (existingBall && !existingBall.pocketed) {
          if (pBall.type === 'cue') {
            cueBallPocketed = true;
          } else {
            pocketedBalls.push({ ...pBall, pocketedBy: player });
            if (pBall.type === '8ball') {
              eightBallPocketed = true;
            } else if (playerAssignment === 'unassigned') {
              // Table is open, any non-cue pocket counts
              ownBallPocketed = true;
            } else if (
              (playerAssignment === 'solids' && pBall.type === 'solid') ||
              (playerAssignment === 'stripes' && pBall.type === 'stripe')
            ) {
              ownBallPocketed = true;
            }
          }
        }
      }
    }

    // Check foul conditions
    let foul = false;
    let foulType: FoulType | undefined;
    let foulReason: string | undefined;

    const firstHitType = this.physics.getFirstBallHitType();
    const railContact = this.physics.getRailContactOccurred();

    // Foul: Cue ball pocketed (scratch)
    if (cueBallPocketed) {
      foul = true;
      foulType = 'scratch';
      foulReason = 'Cue ball pocketed (scratch)';
    }
    // Foul: No ball was hit
    else if (!firstHitType) {
      foul = true;
      foulType = 'no_ball_hit';
      foulReason = 'Cue ball did not hit any ball';
    }
    // Foul: Hit opponent ball first (only when assignment is made)
    else if (playerAssignment !== 'unassigned' && !this.state.isBreakShot) {
      const shouldHitFirst = playerAssignment === 'solids' ? 'solid' : 'stripe';
      // If all own balls pocketed, must hit 8-ball first
      const allOwnPocketed = this.areAllAssignedBallsPocketed(player);
      if (allOwnPocketed) {
        if (firstHitType !== '8ball') {
          foul = true;
          foulType = 'wrong_ball_first';
          foulReason = 'Must hit 8-ball first (all assigned balls pocketed)';
        }
      } else if (firstHitType !== shouldHitFirst && firstHitType !== '8ball') {
        foul = true;
        foulType = 'wrong_ball_first';
        foulReason = `Hit opponent's ball first`;
      }
    }

    // Foul: No rail contact after hit (and no ball pocketed)
    if (!foul && !railContact && pocketedBalls.length === 0 && !cueBallPocketed && firstHitType) {
      foul = true;
      foulType = 'no_rail_contact';
      foulReason = 'No rail contact after hit and no ball pocketed';
    }

    // Check game-ending conditions
    let gameOver = false;
    let winner: BilliardsPlayer | undefined;
    let lossReason: string | undefined;

    if (eightBallPocketed) {
      const allOwnPocketed = this.areAllAssignedBallsPocketed(player);
      if (cueBallPocketed || !allOwnPocketed || foul) {
        // Pocketing 8-ball illegally = loss
        gameOver = true;
        winner = player === 'player1' ? 'player2' : 'player1';
        lossReason = cueBallPocketed
          ? 'Scratched on 8-ball shot'
          : !allOwnPocketed
          ? 'Pocketed 8-ball before clearing assigned balls'
          : 'Fouled on 8-ball shot';
        this.state.gameStatus = { type: 'loss', loser: player, reason: lossReason };
      } else {
        // Legal 8-ball pocket = win
        gameOver = true;
        winner = player;
        this.state.gameStatus = { type: 'win', winner: player, reason: 'Legally pocketed the 8-ball' };
      }
    }

    // Handle assignment (table open logic)
    let assignmentMade = false;
    if (!foul && !gameOver && this.state.tableOpen && pocketedBalls.length > 0 && !this.state.isBreakShot) {
      // Assign based on first legally pocketed ball
      const firstPocketed = pocketedBalls.find((b) => b.type === 'solid' || b.type === 'stripe');
      if (firstPocketed) {
        this.assignPlayerBalls(player, firstPocketed.type === 'solid' ? 'solids' : 'stripes');
        assignmentMade = true;
        this.state.tableOpen = false;
      }
    }

    // After break shot, check if assignment should happen
    if (this.state.isBreakShot && !foul && pocketedBalls.length > 0) {
      // On break, balls are pocketed but table remains open
      // Assignment happens on the NEXT legal pocket
      this.state.isBreakShot = false;
    } else if (this.state.isBreakShot) {
      this.state.isBreakShot = false;
    }

    // Update ball states
    for (const pocketed of pocketedBalls) {
      const ballIndex = this.state.balls.findIndex((b) => b.id === pocketed.id);
      if (ballIndex >= 0) {
        this.state.balls[ballIndex].pocketed = true;
        this.state.balls[ballIndex].pocketedBy = player;

        if (!foul || pocketed.type === '8ball') {
          if (player === 'player1') {
            this.state.player1PocketedBalls.push(pocketed);
          } else {
            this.state.player2PocketedBalls.push(pocketed);
          }
        }
      }
    }

    // Handle cue ball scratch
    if (cueBallPocketed) {
      this.state.ballInHand = true;
      // Reset cue ball to center-left for placement
      const cueBall = this.physics.getBall('cue');
      if (cueBall) {
        cueBall.pocketed = false;
        cueBall.x = TABLE_WIDTH * 0.25;
        cueBall.y = TABLE_HEIGHT / 2;
        cueBall.vx = 0;
        cueBall.vy = 0;
      }
      // Update in state
      const cueBallState = this.state.balls.find((b) => b.type === 'cue');
      if (cueBallState) {
        cueBallState.pocketed = false;
        cueBallState.x = TABLE_WIDTH * 0.25;
        cueBallState.y = TABLE_HEIGHT / 2;
      }
    }

    // If foul (non-scratch), set ball-in-hand for opponent
    if (foul && !cueBallPocketed) {
      this.state.ballInHand = true;
    }

    // Determine if turn continues
    const continueTurn = !foul && ownBallPocketed && !gameOver;

    // Sync physics to state
    this.syncPhysicsToState();

    // Record move
    const result: ShotResult = {
      pocketedBalls,
      cueBallPocketed,
      foul,
      foulType,
      foulReason,
      continueTurn,
      gameOver,
      winner,
      assignmentMade,
    };

    this.state.moveHistory.push({
      moveNumber: this.state.moveHistory.length + 1,
      player,
      shot,
      result,
      timestamp: Date.now(),
    });

    this.state.lastMoveAt = Date.now();

    // Advance turn if needed
    if (!continueTurn && !gameOver) {
      this.advanceTurn();
    }

    return result;
  }

  // =========================================================================
  // Game Rules
  // =========================================================================

  private assignPlayerBalls(player: BilliardsPlayer, assignment: 'solids' | 'stripes'): void {
    if (player === 'player1') {
      this.state.player1Assignment = assignment;
      this.state.player2Assignment = assignment === 'solids' ? 'stripes' : 'solids';
    } else {
      this.state.player2Assignment = assignment;
      this.state.player1Assignment = assignment === 'solids' ? 'stripes' : 'solids';
    }
  }

  private areAllAssignedBallsPocketed(player: BilliardsPlayer): boolean {
    const assignment = this.getPlayerAssignment(player);
    if (assignment === 'unassigned') return false;

    const targetType: BallType = assignment === 'solids' ? 'solid' : 'stripe';
    const remaining = this.state.balls.filter(
      (b) => b.type === targetType && !b.pocketed
    );
    return remaining.length === 0;
  }

  private advanceTurn(): void {
    this.state.currentTurn =
      this.state.currentTurn === 'player1' ? 'player2' : 'player1';
  }

  // =========================================================================
  // Ball-in-Hand
  // =========================================================================

  /**
   * Place the cue ball at specified position (ball-in-hand).
   * Returns false if position is invalid (overlaps with another ball).
   */
  placeCueBall(x: number, y: number): boolean {
    const minX = CUSHION_WIDTH + CUE_BALL_RADIUS;
    const maxX = TABLE_WIDTH - CUSHION_WIDTH - CUE_BALL_RADIUS;
    const minY = CUSHION_WIDTH + CUE_BALL_RADIUS;
    const maxY = TABLE_HEIGHT - CUSHION_WIDTH - CUE_BALL_RADIUS;

    // Clamp to table bounds
    const clampedX = Math.max(minX, Math.min(maxX, x));
    const clampedY = Math.max(minY, Math.min(maxY, y));

    // Check overlap with other balls
    const activeBalls = this.state.balls.filter((b) => !b.pocketed && b.type !== 'cue');
    for (const ball of activeBalls) {
      const dx = clampedX - ball.x;
      const dy = clampedY - ball.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < CUE_BALL_RADIUS + ball.radius + 1) {
        return false;
      }
    }

    this.physics.placeCueBall(clampedX, clampedY);

    // Update state
    const cueBall = this.state.balls.find((b) => b.type === 'cue');
    if (cueBall) {
      cueBall.x = clampedX;
      cueBall.y = clampedY;
      cueBall.pocketed = false;
    }

    this.state.ballInHand = false;
    return true;
  }

  // =========================================================================
  // State Sync
  // =========================================================================

  private syncPhysicsToState(): void {
    for (const ball of this.state.balls) {
      const pBall = this.physics.getBall(ball.id);
      if (pBall && !ball.pocketed) {
        ball.x = pBall.x;
        ball.y = pBall.y;
      }
    }
  }

  // =========================================================================
  // Getters
  // =========================================================================

  getGameState(): BilliardsGameState {
    return { ...this.state };
  }

  isGameOver(): boolean {
    return this.state.gameStatus.type !== 'active';
  }

  getCurrentPlayer(): BilliardsPlayer {
    return this.state.currentTurn;
  }

  getPlayerAssignment(player: BilliardsPlayer): PlayerAssignment {
    return player === 'player1'
      ? this.state.player1Assignment
      : this.state.player2Assignment;
  }

  getActiveBalls(): BilliardsBall[] {
    return this.state.balls.filter((b) => !b.pocketed);
  }

  getCueBall(): BilliardsBall | undefined {
    return this.state.balls.find((b) => b.type === 'cue');
  }

  isBallInHand(): boolean {
    return this.state.ballInHand;
  }

  isTableOpen(): boolean {
    return this.state.tableOpen;
  }
}
